<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import { useConversationLabels } from 'dashboard/composables/useConversationLabels';

const { t } = useI18n();

const [showLabelsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);

const {
  accountLabels,
  savedLabels,
  addLabelToConversation,
  removeLabelFromConversation,
} = useConversationLabels();

const labelMenuItems = computed(() => {
  return accountLabels.value.map(label => ({
    icon: 'i-lucide-tag',
    label: label.title,
    value: label.title,
    color: label.color,
    selected: savedLabels.value.includes(label.title),
  }));
});

const handleLabelClick = item => {
  if (item.selected) {
    removeLabelFromConversation(item.value);
  } else {
    addLabelToConversation({ title: item.value });
  }
};

const activeLabelsCount = computed(() => savedLabels.value.length);
</script>

<template>
  <div
    v-on-clickaway="closeDropdown"
    class="relative flex items-center labels-action"
  >
    <Button
      v-tooltip="$t('CONVERSATION.HEADER.LABELS_ACTION')"
      size="sm"
      color="slate"
      icon="i-lucide-tag"
      class="rounded-lg shadow outline-1 outline outline-n-container"
      :badge="activeLabelsCount > 0 ? activeLabelsCount : null"
      @click="toggleDropdown()"
    />
    <div
      v-if="showLabelsDropdown"
      class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-10 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-0 max-w-[16rem] min-w-[12rem]"
    >
      <div class="mb-2 px-2 py-1 text-xs font-medium text-n-slate-11">
        {{ $t('CONVERSATION.HEADER.SELECT_LABELS') }}
      </div>
      <div class="max-h-[300px] overflow-y-auto">
        <button
          v-for="item in labelMenuItems"
          :key="item.value"
          class="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded hover:bg-n-alpha-2 transition-colors text-left"
          :class="{
            'bg-n-alpha-2': item.selected,
          }"
          @click="handleLabelClick(item)"
        >
          <span
            class="w-3 h-3 rounded-sm flex-shrink-0"
            :style="{ backgroundColor: item.color }"
          />
          <span class="flex-1 truncate text-n-slate-12">{{ item.label }}</span>
          <fluent-icon
            v-if="item.selected"
            icon="checkmark"
            size="16"
            class="text-n-blue-9 flex-shrink-0"
          />
        </button>
      </div>
      <div
        v-if="labelMenuItems.length === 0"
        class="px-2 py-4 text-center text-sm text-n-slate-11"
      >
        {{ $t('CONVERSATION.LABELS.NO_LABELS_FOUND') }}
      </div>
    </div>
  </div>
</template>
